import React, { useState } from 'react'
import {
  FullBorderRadiusType,
  BorderRadiusType,
  BorderType,
} from 'common/types/styleTypes'
import { ExpandButton } from 'src/editor/shared/ui-lib/expand-button'
import { BorderRadiusIcon } from 'src/editor/shared/icons/border-radius-icon'
import { OptionWrapper } from 'src/editor/layout/sidebar/settings/option-wrapper'
import { NumberInput } from 'src/editor/shared/ui-lib/input/number-input'
import { getMobileOrDesktop } from 'src/shared/utils/get-mobile-or-desktop'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'

export const MAX_RADIUS_VAL = 100

const isBorderRadiusFull = (
  radius: BorderRadiusType | FullBorderRadiusType | undefined,
): radius is FullBorderRadiusType => typeof radius === 'object'

const areAllBorderRadiiEqual = (
  radius: number | FullBorderRadiusType | undefined,
): radius is FullBorderRadiusType => {
  if (typeof radius === 'number' || !radius) {
    return true
  }
  const {
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomRightRadius,
    borderBottomLeftRadius,
  } = radius

  return (
    borderTopLeftRadius === borderTopRightRadius &&
    borderTopLeftRadius === borderBottomRightRadius &&
    borderTopLeftRadius === borderBottomLeftRadius
  )
}

interface BorderRadiusControlProps {
  border: Partial<BorderType> | undefined
  mobileBorder?: Partial<BorderType> | undefined
  update: (value: Partial<BorderType>) => void
  mobileUpdate?: (value: Partial<BorderType>) => void
  label?: string
  withoutExpandButton?: boolean
}

export const BorderRadiusControl = ({
  border,
  mobileBorder,
  update,
  mobileUpdate,
  label = 'website_styles_settings.border_radius.label',
  withoutExpandButton = false,
}: BorderRadiusControlProps) => {
  const { isMobile } = useDeviceModeContext()

  const resultBorder = getMobileOrDesktop(border, mobileBorder, isMobile)
  const resultUpdate = getMobileOrDesktop(update, mobileUpdate, isMobile)

  const [isVisibleFullSetting, setIsVisibleFullSetting] = useState(
    areAllBorderRadiiEqual(resultBorder?.radius),
  )

  const handleUpdate =
    (type: keyof FullBorderRadiusType | 'all') =>
    (value = 0) => {
      if (type === 'all') {
        resultUpdate({
          ...resultBorder,
          radius: {
            borderTopLeftRadius: value || 0,
            borderTopRightRadius: value || 0,
            borderBottomRightRadius: value || 0,
            borderBottomLeftRadius: value || 0,
          },
        })
      } else if (isBorderRadiusFull(resultBorder?.radius)) {
        resultUpdate({
          ...resultBorder,
          radius: {
            ...resultBorder.radius,
            [type]: value,
          },
        })
      }
    }

  const switchSettings = () => {
    const radius =
      typeof resultBorder?.radius === 'object'
        ? resultBorder?.radius?.borderTopLeftRadius
        : resultBorder?.radius

    resultUpdate({
      ...resultBorder,
      radius: {
        borderTopLeftRadius: radius || 0,
        borderTopRightRadius: radius || 0,
        borderBottomRightRadius: radius || 0,
        borderBottomLeftRadius: radius || 0,
      },
    })
    setIsVisibleFullSetting(!isVisibleFullSetting)
  }

  return (
    <OptionWrapper labelText={label}>
      <div className="flex gap-[6px]">
        <div className="grid grow-1 grid-cols-2 gap-3">
          {isVisibleFullSetting || withoutExpandButton ? (
            <NumberInput
              startElement={<BorderRadiusIcon className="text-iconTertiary" />}
              className="col-span-2"
              onChange={handleUpdate('all')}
              value={
                isBorderRadiusFull(resultBorder?.radius)
                  ? (resultBorder.radius.borderTopLeftRadius ?? 0)
                  : 0
              }
              min={0}
              max={MAX_RADIUS_VAL}
            />
          ) : (
            <>
              <NumberInput
                startElement={
                  <BorderRadiusIcon className="text-iconTertiary" />
                }
                onChange={handleUpdate('borderTopLeftRadius')}
                value={
                  isBorderRadiusFull(resultBorder?.radius)
                    ? (resultBorder.radius.borderTopLeftRadius ?? 0)
                    : 0
                }
                min={0}
                max={MAX_RADIUS_VAL}
              />
              <NumberInput
                startElement={
                  <BorderRadiusIcon className="rotate-90 text-iconTertiary" />
                }
                onChange={handleUpdate('borderTopRightRadius')}
                value={
                  isBorderRadiusFull(resultBorder?.radius)
                    ? (resultBorder.radius.borderTopRightRadius ?? 0)
                    : 0
                }
                min={0}
                max={MAX_RADIUS_VAL}
              />
              <NumberInput
                startElement={
                  <BorderRadiusIcon className="rotate-[270deg] text-iconTertiary" />
                }
                onChange={handleUpdate('borderBottomLeftRadius')}
                value={
                  isBorderRadiusFull(resultBorder?.radius)
                    ? (resultBorder.radius.borderBottomLeftRadius ?? 0)
                    : 0
                }
                min={0}
                max={MAX_RADIUS_VAL}
              />
              <NumberInput
                startElement={
                  <BorderRadiusIcon className="rotate-180 text-iconTertiary" />
                }
                onChange={handleUpdate('borderBottomRightRadius')}
                value={
                  isBorderRadiusFull(resultBorder?.radius)
                    ? (resultBorder.radius.borderBottomRightRadius ?? 0)
                    : 0
                }
                min={0}
                max={MAX_RADIUS_VAL}
              />
            </>
          )}
        </div>
        {!withoutExpandButton && (
          <ExpandButton
            className="relative top-[5.5px]"
            isActive={isVisibleFullSetting}
            onClick={switchSettings}
          />
        )}
      </div>
    </OptionWrapper>
  )
}
